// RetailCliQ ERP — Industry Configuration
// Each industry has: id, name, accent, tagline, headline, stats, features, quote, modules

export const INDUSTRY_CONFIG = {
  apparel: {
    id: 'apparel',
    name: 'Apparel & Garments',
    icon: 'Shirt',
    accent: '#E879F9',
    accentDark: '#C026D3',
    bgGlow: 'rgba(232,121,249,0.12)',
    headline: 'Fashion Retail,',
    headlineAccent: 'Reimagined',
    tagline: 'Smart Retail Solutions for Apparel & Garments',
    description: 'From fast fashion to luxury — manage every style, size and season with intelligence.',
    stats: [
      { value: '50K+', label: 'SKUs Managed' },
      { value: '250+', label: 'Collections' },
      { value: '2500+', label: 'Brands' },
      { value: 'GST', label: 'Compliant' },
    ],
    features: [
      'Season & Collection Management',
      'Barcode / RFID Integration',
      'Style-Size-Color Matrix',
      'Return & Exchange Handling',
      'Loyalty & Offers Engine',
    ],
    quote: 'Fashion fades, but great retail operations are always in style.',
    security: 'RFID Ready · PCI-DSS · GST · Audit Logs',
    modules: ['inventory', 'pos', 'finance', 'crm', 'analytics', 'hr', 'procurement', 'audit'],
  },

  jewellery: {
    id: 'jewellery',
    name: 'Jewellery',
    icon: 'Gem',
    accent: '#F59E0B',
    accentDark: '#D97706',
    bgGlow: 'rgba(245,158,11,0.12)',
    headline: 'Gold to Gems —',
    headlineAccent: 'Full ERP Control',
    tagline: 'Smart Retail Solutions for Jewellery Stores',
    description: 'Live gold rates, hallmarking, making charges and HUID compliance — built in.',
    stats: [
      { value: '25K+', label: 'SKUs Managed' },
      { value: 'HUID', label: 'Compliant' },
      { value: '500+', label: 'Artisans' },
      { value: 'BIS', label: 'Certified' },
    ],
    features: [
      'Live Gold & Silver Rate',
      'Karat / Purity Tracking',
      'Making Charge Calculator',
      'HUID Hallmarking Compliance',
      'Repair Order Management',
    ],
    quote: 'Every carat counts. Manage your treasure intelligently.',
    security: 'BIS · HUID · MFA · AML Ready',
    modules: ['inventory', 'pos', 'finance', 'crm', 'analytics', 'procurement', 'audit'],
  },

  footwear: {
    id: 'footwear',
    name: 'Footwear',
    icon: 'Footprints',
    accent: '#EF4444',
    accentDark: '#DC2626',
    bgGlow: 'rgba(239,68,68,0.12)',
    headline: 'Step Into',
    headlineAccent: 'Smarter Operations',
    tagline: 'Smart Retail Solutions for Footwear Retail',
    description: 'Size-color matrix, season management and vendor analytics built for footwear.',
    stats: [
      { value: '15K+', label: 'Style Variants' },
      { value: '1200+', label: 'Brands' },
      { value: '2500+', label: 'Stores' },
      { value: '99.9%', label: 'Uptime' },
    ],
    features: [
      'Size × Color Matrix',
      'Season Management',
      'Return & Exchange Module',
      'Vendor Performance Analytics',
      'Brand-wise Sales Reports',
    ],
    quote: 'Every great journey starts with the right step forward.',
    security: 'MFA · PCI-DSS · GST · Audit',
    modules: ['inventory', 'pos', 'crm', 'finance', 'analytics', 'procurement'],
  },

  hospitality: {
    id: 'hospitality',
    name: 'Hospitality',
    icon: 'Hotel',
    accent: '#06B6D4',
    accentDark: '#0891B2',
    bgGlow: 'rgba(6,182,212,0.12)',
    headline: 'Hospitality at',
    headlineAccent: 'Its Finest',
    tagline: 'Smart ERP for Hotels, Restaurants & Resorts',
    description: 'Reservations, F&B, housekeeping and channel management in one unified platform.',
    stats: [
      { value: '500+', label: 'Properties' },
      { value: '87%', label: 'Avg Occupancy' },
      { value: '4.8★', label: 'Guest Rating' },
      { value: '25K+', label: 'Rooms Managed' },
    ],
    features: [
      'Reservation & Check-in/Out',
      'F&B & Room Service POS',
      'Housekeeping Management',
      'OTA Channel Management',
      'Revenue & Yield Analytics',
    ],
    quote: 'Creating exceptional guest experiences, one stay at a time.',
    security: 'PCI-DSS · GDPR · MFA · OTA Ready',
    modules: ['pos', 'analytics', 'finance', 'hr', 'procurement', 'settings'],
  },

  bakery: {
    id: 'bakery',
    name: 'Bakery & Food',
    icon: 'ChefHat',
    accent: '#F97316',
    accentDark: '#EA580C',
    bgGlow: 'rgba(249,115,22,0.12)',
    headline: 'Baked Fresh.',
    headlineAccent: 'Managed Smart.',
    tagline: 'Smart ERP for Bakeries & Food Retail',
    description: 'Recipe management, production planning and online order integration for bakeries.',
    stats: [
      { value: '2500+', label: 'Recipes' },
      { value: '800+', label: 'Outlets' },
      { value: '50K', label: 'Orders/Day' },
      { value: '-40%', label: 'Wastage' },
    ],
    features: [
      'Recipe & COGS Management',
      'Production Planning & Yield',
      'Expiry & Freshness Tracking',
      'Zomato / Swiggy Integration',
      'Daily Wastage Control',
    ],
    quote: 'Fresh ingredients, smart management — the perfect recipe for success.',
    security: 'FSSAI · GST · Hygiene Certified',
    modules: ['inventory', 'pos', 'finance', 'analytics', 'hr', 'settings'],
  },

  furniture: {
    id: 'furniture',
    name: 'Furniture',
    icon: 'Sofa',
    accent: '#A78BFA',
    accentDark: '#7C3AED',
    bgGlow: 'rgba(167,139,250,0.12)',
    headline: 'Design, Manufacture,',
    headlineAccent: 'Deliver — Seamlessly',
    tagline: 'Smart ERP for Furniture & Home Décor',
    description: 'BOQ, BOM, delivery scheduling and showroom management for furniture retailers.',
    stats: [
      { value: '8K+', label: 'Products' },
      { value: '200+', label: 'Showrooms' },
      { value: '15K', label: 'Custom Orders' },
      { value: '500+', label: 'Suppliers' },
    ],
    features: [
      'BOQ & BOM Management',
      'Custom Order Configurator',
      'Delivery Scheduling',
      'EMI & Finance Integration',
      'Warranty & AMC Tracking',
    ],
    quote: 'Good furniture tells a story. Great management writes it.',
    security: 'MFA · GST · EMI · Audit Logs',
    modules: ['inventory', 'finance', 'crm', 'procurement', 'analytics', 'hr'],
  },

  electronics: {
    id: 'electronics',
    name: 'Electronics',
    icon: 'Cpu',
    accent: '#3B82F6',
    accentDark: '#2563EB',
    bgGlow: 'rgba(59,130,246,0.12)',
    headline: 'Powering Retail',
    headlineAccent: 'with Intelligence',
    tagline: 'Smart ERP for Electronics & Appliances Retail',
    description: 'IMEI tracking, warranty, EMI and exchange programs — all in one platform.',
    stats: [
      { value: '75K+', label: 'SKUs' },
      { value: '1200+', label: 'Service Centers' },
      { value: '500+', label: 'Brands' },
      { value: 'HSN', label: 'GST Ready' },
    ],
    features: [
      'IMEI / Serial Tracking',
      'Warranty Management',
      'EMI & Finance Processing',
      'Exchange & Buy-Back Programs',
      'Service Center Integration',
    ],
    quote: 'Technology that sells technology. That is the RetailCliQ edge.',
    security: 'IMEI · BIS · PCI-DSS · Warranty',
    modules: ['inventory', 'pos', 'finance', 'crm', 'analytics', 'procurement', 'audit', 'settings'],
  },

  mobile: {
    id: 'mobile',
    name: 'Mobile Retail',
    icon: 'Smartphone',
    accent: '#EC4899',
    accentDark: '#DB2777',
    bgGlow: 'rgba(236,72,153,0.12)',
    headline: 'Mobile First.',
    headlineAccent: 'Business Always.',
    tagline: 'Smart ERP for Mobile & Telecom Retail',
    description: 'IMEI tracking, MNP processing, activations and telecom commissions — unified.',
    stats: [
      { value: '200+', label: 'Brands' },
      { value: '5000+', label: 'Models' },
      { value: '25K', label: 'Activations/Day' },
      { value: '8K', label: 'Repair Jobs' },
    ],
    features: [
      'IMEI Tracking & Activation',
      'MNP Processing',
      'Telecom Commission Mgmt',
      'Insurance Integration',
      'Buy-Back & Exchange Programs',
    ],
    quote: 'Every connection matters. Manage them all from one screen.',
    security: 'IMEI · MNP · PCI-DSS · Telecom Compliant',
    modules: ['inventory', 'pos', 'finance', 'crm', 'analytics', 'procurement'],
  },
};

export const INDUSTRY_LIST = Object.values(INDUSTRY_CONFIG);

export const getIndustryConfig = (id) => INDUSTRY_CONFIG[id] || null;

export const DEFAULT_INDUSTRY_CONFIG = {
  id: 'retail',
  name: 'Retail',
  icon: 'Store',
  accent: '#3DD56D',
  accentDark: '#22C55E',
  bgGlow: 'rgba(61,213,109,0.12)',
  headline: 'The Future of',
  headlineAccent: 'Retail ERP',
  tagline: 'Smart Retail Solutions',
  description: 'Next-generation enterprise platform powering retail operations at scale.',
  stats: [
    { value: '2500+', label: 'Stores Live' },
    { value: '50+', label: 'Modules' },
    { value: '99.9%', label: 'Uptime' },
    { value: 'GST', label: 'Compliant' },
  ],
  features: [
    'AI-Powered Analytics',
    'Real-time Inventory',
    'Multi-Branch Support',
    'Offline-Ready POS',
    'GST Filing Integrated',
  ],
  quote: 'Success is the sum of small efforts, repeated day in and day out.',
  security: 'MFA Enabled · SSO-Ready · ISO 27001',
  modules: ['finance', 'hr', 'inventory', 'pos', 'crm', 'analytics', 'procurement', 'payroll', 'audit', 'settings'],
};
